<script setup>
import { computed, onUnmounted, ref } from 'vue';

const { isShareSheetOpen, currentArticle, tagEvent, toggleModals, shareUrl } = defineProps({
	isShareSheetOpen: Boolean,
	currentArticle: Object,
	tagEvent: Function,
	toggleModals: Function,
	shareUrl: String,
});

const cleanUrl = computed(() => {
	return shareUrl || 'https://designexternship.com/?article='+currentArticle.id;
})
const encodedUrl = computed(() => {
	return encodeURIComponent(cleanUrl.value);
})
const encodedTitle = computed(() => {
	return encodeURIComponent(currentArticle.title);
})
const twitterShare = computed(() => {
	return `https://x.com/intent/post?url=${encodedUrl.value}&text=${encodedTitle.value}`;
})
const facebookShare = computed(() => {
	return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl.value}`;
})
const linkedInShare = computed(() => {
	return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl.value}`;
})
const threadsShare = computed(() => {
	return `https://www.threads.net/intent/post?text=${encodedUrl.value}`;
})
const blueskyShare = computed(() => {
	return `https://bsky.app/intent/compose?text=${encodedTitle.value}%20${encodedUrl.value}`;
})
const mastodonShare = computed(() => {
	return `https://toot.kytta.dev/?text=${encodedTitle.value}%20${encodedUrl.value}`;
})

function toggleShareSheet() {
	if (isShareSheetOpen) {
		toggleModals();
	} else {
		toggleModals("share-sheet");
	}
}

function openPopup(url, source) {
	const width = 600;
	const height = 400;
	const left = (window.innerWidth - width) / 2;
	const top = (window.innerHeight - height) / 2;

	window.open(
		url,
		'_blank',
		`width=${width},height=${height},top=${top},left=${left}`
	);
	tagEvent({where: "share_article", what: source});
	toggleShareSheet();
}

const copied = ref(false);
let copiedResetTimer;

async function copyToClipboard() {
	try {
		await navigator.clipboard.writeText(cleanUrl.value);
		tagEvent({ where: "share_article", what: 'copy_link' });
		copied.value = true;
		window.clearTimeout(copiedResetTimer);
		copiedResetTimer = window.setTimeout(() => {
			copied.value = false;
		}, 3000);
	} catch (err) {
		console.error('Failed to copy: ', err);
	}
}

onUnmounted(() => window.clearTimeout(copiedResetTimer));

</script>
<template>
<dialog
	id="share-sheet"
	:data-open="isShareSheetOpen"
	@cancel.prevent="toggleModals()"
>
	<header>
		<h4>Share</h4>
		<a
			class="btn secondary"
			@click="toggleShareSheet()"
			rel="noopener noreferrer"
		>
			<i class="fa-regular fa-close" title="close share window"></i>
		</a>
	</header>
	<p>Share on Social Media</p>
	<div class="social-icons">
		<a
			@click="openPopup(twitterShare, 'twitter')"
			class="btn secondary"
			target="_blank"
			rel="noopener noreferrer"
		>
			<i class="fa-brands fa-x-twitter" title="Share on Twitter"></i>
		</a>
		<a
			@click="openPopup(facebookShare, 'facebook')"
			class="btn secondary"
			target="_blank"
			rel="noopener noreferrer"
		>
			<i class="fa-brands fa-facebook" title="Share on Facebook"></i>
		</a>
		<a
			@click="openPopup(linkedInShare, 'linkedIn')"
			class="btn secondary"
			target="_blank"
			rel="noopener noreferrer"
		>
			<i class="fa-brands fa-linkedin-in" title="Share on LinkedIn"></i>
		</a>
		<a
			@click="openPopup(threadsShare, 'threads')"
			class="btn secondary"
			target="_blank"
			rel="noopener noreferrer"
		>
			<i class="fa-brands fa-threads" title="Share on Threads"></i>
		</a>
		<a
			@click="openPopup(blueskyShare, 'bluesky')"
			class="btn secondary"
			target="_blank"
			rel="noopener noreferrer"
		>
			<i class="fa-brands fa-bluesky" title="Share on Bluesky"></i>
		</a>
		<a
			@click="openPopup(mastodonShare, 'mastodon')"
			class="btn secondary"
			target="_blank"
			rel="noopener noreferrer"
		>
			<i class="fa-brands fa-mastodon" title="Share on Mastodon"></i>
		</a>
	</div>
	<p>Share link</p>
	<a
		@click="copyToClipboard()"
		:class="['btn wide', copied ? 'primary' : 'secondary']"
		rel="noopener noreferrer"
		aria-live="polite"
	>
		<span>{{ copied ? 'Copied' : 'Copy Link' }}</span>
		<i :class="['fa-regular', copied ? 'fa-check' : 'fa-link']" aria-hidden="true"></i>
	</a>
</dialog>
</template>

<style lang="scss" scoped>
#share-sheet {
	background-color: var(--bg3);
	border: 1px solid var(--border);
	border-radius: 20px;
	box-shadow: var(--floatingShadow);
	color: var(--text1);
	outline: none;
	padding: 16px;
	position: fixed;
	left: calc(100vw + 16px);
	right: calc(200vw - 16px);
	bottom: 120px;
	top: auto;
	margin: 0 -6px 0;
	width: calc(var(--main-content-width) - 20px - (var(--container-padding) + 1) * 2 - 1px);
	z-index: 4;
}
#sidebar.collapsed + * #share-sheet {
	left: 16px;
	right: 16px;
}
@media (min-width: 768px) {
	#share-sheet {
		right: 10px;
		bottom: 64px;
		left: auto;
		margin: 0;
		width: calc(var(--main-content-width) - ((var(--container-padding) + 13px) * 2));
	}
}
@media (min-width: 1024px) {
	#share-sheet {
		inset: 0;
		margin: auto;
		width: min(420px, calc(100vw - 32px));
		max-height: calc(100dvh - 32px);
		z-index: 1000;
	}
	#share-sheet::backdrop { background: rgb(0 0 0 / 45%); }
}

#share-sheet * {
	user-select: none;
	-webkit-user-select: none;
}
#share-sheet header {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	padding-bottom: 14px;
	border-bottom: 1px solid var(--border);
}
#share-sheet h4 {
	font-size: 1.5rem;
	font-weight: 300;
	margin: 0;
}
#share-sheet .btn {
	border: unset;
}
#share-sheet header .btn {
	border-radius: 8px;
	padding: 6px 8px;
}
#share-sheet .social-icons {
	display: flex;
	flex-flow: row;
	justify-content: space-between;
	align-items: flex-start;
}
#share-sheet .social-icons .btn {
	border-radius: 16px;
	font-size: 1.4rem;
	text-align: center;
	padding: 0;

	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 48px;
	height: 48px;
}
</style>
